"use client";

import { motion } from "motion/react";
import { createContext, createElement, useCallback, useContext, useEffect, useId, useMemo, useRef, useState, type ComponentPropsWithoutRef, type ComponentType, type CSSProperties, type ElementType, type JSX, type ReactNode, type SVGProps } from "react";
import { createPortal } from "react-dom";

type ModalSizeStyle = Pick<CSSProperties, "width" | "height" | "minWidth" | "minHeight" | "maxWidth" | "maxHeight" | "aspectRatio">;
type OverlayOrigin = { left: number; top: number; width: number; height: number; };
type OverlayItem = { id: string; render: ReactNode | null; onClose: () => void; clickToExit: boolean; };
type OverlayContextValue = { overlays: OverlayItem[]; openFrom: OverlayOrigin | undefined; addOverlay: (id: string, render: ReactNode | null, onClose?: () => void, clickToExit?: boolean) => void; removeOverlay: (id: string, callClose?: boolean) => void; closeTopOverlay: () => void; };
type OverlaySubContextValue = { quit: () => void; invalid?: boolean; };

const OverlayContext = createContext<OverlayContextValue | null>(null);
const OverlaySubContext = createContext<OverlaySubContextValue>({ quit: () => undefined, invalid: true });

function useOverlayContext() {
    const context = useContext(OverlayContext);
    if (!context) throw new Error("Overlay components must be rendered inside OverlayProvider.");
    return context;
}

function CloseIcon(props: SVGProps<SVGSVGElement>) {
    return <svg viewBox="0 0 24 24" aria-hidden="true" {...props}><path fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" d="m6.5 6.5 11 11m0-11-11 11" /></svg>;
}

export function OverlayProvider({ children }: { children?: ReactNode; }) {
    const [overlays, setOverlays] = useState<OverlayItem[]>([]);
    const [openFrom, setOpenFrom] = useState<OverlayOrigin>();
    const pressedBackdropRef = useRef<HTMLElement | null>(null);
    const overlaysRef = useRef<OverlayItem[]>([]);
    const closingRef = useRef(new Set<string>());
    const previousOverflowRef = useRef<string | null>(null);

    useEffect(() => { overlaysRef.current = overlays; }, [overlays]);

    const removeOverlay = useCallback((id: string, callClose = true) => {
        const found = overlaysRef.current.find(overlay => overlay.id === id);
        if (!found) return;
        if (callClose && !closingRef.current.has(id)) {
            closingRef.current.add(id);
            found.onClose();
        }
        setOverlays(current => current.filter(overlay => overlay.id !== id));
    }, []);

    const closeTopOverlay = useCallback(() => {
        const found = overlaysRef.current.at(-1);
        if (found) removeOverlay(found.id);
    }, [removeOverlay]);

    const addOverlay = useCallback((id: string, render: ReactNode | null, onClose: () => void = () => undefined, clickToExit = true) => {
        closingRef.current.delete(id);
        setOverlays(current => [...current.filter(overlay => overlay.id !== id), { id, render, onClose, clickToExit }]);
    }, []);

    useEffect(() => {
        const captureOrigin = (event: PointerEvent) => {
            if (!(event.target instanceof HTMLElement)) return;
            const target = event.target.closest<HTMLElement>("[data-overlay-target]");
            if (!target) {
                setOpenFrom(undefined);
                return;
            }
            const rect = target.getBoundingClientRect();
            setOpenFrom({ left: rect.left, top: rect.top, width: rect.width, height: rect.height });
        };
        window.addEventListener("pointerdown", captureOrigin, true);
        return () => window.removeEventListener("pointerdown", captureOrigin, true);
    }, []);

    useEffect(() => {
        if (overlays.length === 0) {
            closingRef.current.clear();
            if (previousOverflowRef.current !== null) {
                document.documentElement.style.overflow = previousOverflowRef.current;
                previousOverflowRef.current = null;
            }
            return;
        }

        if (previousOverflowRef.current === null) previousOverflowRef.current = document.documentElement.style.overflow;
        document.documentElement.style.overflow = "hidden";

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") closeTopOverlay();
        };

        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [closeTopOverlay, overlays.length]);

    useEffect(() => () => {
        if (previousOverflowRef.current !== null) document.documentElement.style.overflow = previousOverflowRef.current;
    }, []);

    const value = useMemo<OverlayContextValue>(() => ({ overlays, openFrom, addOverlay, removeOverlay, closeTopOverlay }), [addOverlay, closeTopOverlay, openFrom, overlays, removeOverlay]);

    return (
        <OverlayContext.Provider value={value}>
            <div data-overlay-root className="contents">
                <div id="overlay-controller" data-overlay-bg data-overlay-controller data-activated={overlays.length > 0} onPointerDown={event => { if (event.target instanceof HTMLElement && event.target.hasAttribute("data-overlay-bg")) pressedBackdropRef.current = event.target; }} onPointerUp={event => {
                    if (event.target instanceof HTMLElement && event.target.hasAttribute("data-overlay-bg") && event.target === pressedBackdropRef.current) {
                        const found = overlaysRef.current.at(-1);
                        if (found?.clickToExit) closeTopOverlay();
                    }
                    pressedBackdropRef.current = null;
                }} className="fixed inset-0 z-999 overflow-hidden bg-bg/75 opacity-0 backdrop-blur-md transition-[opacity,backdrop-filter] duration-300 ease-out data-[activated=false]:pointer-events-none data-[activated=true]:opacity-100">
                    {overlays.map(overlay => <span key={overlay.id} className="contents">{overlay.render}</span>)}
                </div>
                {children}
            </div>
        </OverlayContext.Provider>
    );
}

export function useOverlay<T extends unknown[]>(content: ReactNode | ((...args: T) => ReactNode), onClose?: () => void) {
    const { addOverlay } = useOverlayContext();
    const id = useId();
    return (...args: T) => addOverlay(id, <OverlaySub oid={id}><InternalOverlay overlayId={id}>{typeof content === "function" ? content(...args) : content}</InternalOverlay></OverlaySub>, onClose);
}

export function useOverlayQuit() {
    return useContext(OverlaySubContext).quit;
}

export function useIsOverlay() {
    return !useContext(OverlaySubContext).invalid;
}

function InternalOverlay({ children, overlayId }: { children?: ReactNode; overlayId?: string; }) {
    const { closeTopOverlay, openFrom, overlays } = useOverlayContext();
    const isTop = overlayId == null || overlays.at(-1)?.id === overlayId;
    const transformOrigin = openFrom ? `${openFrom.left + openFrom.width / 2}px ${openFrom.top + openFrom.height / 2}px` : "50% 50%";
    return (
        <motion.div data-top={isTop} aria-hidden={!isTop} initial={{ opacity: 0, scale: 0.985 }} animate={{ opacity: isTop ? 1 : 0, scale: isTop ? 1 : 0.99 }} transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }} style={{ transformOrigin }} className="fixed inset-0 isolate overflow-auto bg-bg text-text-primary data-[top=false]:pointer-events-none data-[top=false]:invisible">
            <CloseButton label="Close overlay" onClick={closeTopOverlay} className="right-4 top-4" />
            {children}
        </motion.div>
    );
}

function InternalModal({ children, style, overlayId }: { children?: ReactNode; style: ModalSizeStyle; overlayId?: string; }) {
    const { closeTopOverlay, openFrom, overlays } = useOverlayContext();
    const isTop = overlayId == null || overlays.at(-1)?.id === overlayId;
    const transformOrigin = openFrom ? `${openFrom.left + openFrom.width / 2}px ${openFrom.top + openFrom.height / 2}px` : "50% 50%";
    return (
        <div data-valid data-top={isTop} className="fixed inset-0 grid place-items-center p-3 data-[top=false]:pointer-events-none data-[top=false]:invisible sm:p-6">
            <motion.div role="dialog" aria-modal="true" aria-hidden={!isTop} initial={{ opacity: 0, scale: 0.94, y: 14 }} animate={{ opacity: isTop ? 1 : 0, scale: isTop ? 1 : 0.98, y: isTop ? 0 : 10 }} transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }} style={{ ...style, transformOrigin }} className="relative max-h-full overflow-hidden rounded-[1.75rem] border border-border bg-surface text-text-primary shadow-2xl">
                <CloseButton label="Close modal" onClick={closeTopOverlay} className="right-3 top-3 sm:right-4 sm:top-4" />
                {children}
            </motion.div>
        </div>
    );
}

function CloseButton({ label, onClick, className }: { label: string; onClick: () => void; className: string; }) {
    return <button type="button" aria-label={label} onClick={onClick} className={`absolute z-30 grid size-9 place-items-center rounded-full border border-border bg-bg/80 text-text-primary/55 shadow-lg backdrop-blur-xl transition duration-200 hover:bg-bg hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-text-primary/20 ${className}`}><CloseIcon className="size-4" /></button>;
}

function OverlaySub({ oid, children }: { oid: string; children: ReactNode; }) {
    const { removeOverlay } = useOverlayContext();
    const value = useMemo<OverlaySubContextValue>(() => ({ quit: () => removeOverlay(oid) }), [oid, removeOverlay]);
    return <OverlaySubContext.Provider value={value}>{children}</OverlaySubContext.Provider>;
}

function useNearestOverlayController() {
    const [controller, setController] = useState<HTMLElement | null>(null);
    const anchorRef = useCallback((node: HTMLSpanElement | null) => {
        if (!node) return;
        const root = node.closest<HTMLElement>("[data-overlay-root]");
        setController(root?.querySelector<HTMLElement>("[data-overlay-controller]") ?? null);
    }, []);
    return { anchorRef, controller };
}

type IntrinsicTag = keyof JSX.IntrinsicElements;
type OverlayProps<T extends IntrinsicTag> = { children?: ReactNode | undefined; onClose?: (() => void) | undefined; clickToExit?: boolean | undefined; } & Omit<ComponentPropsWithoutRef<T>, "children" | "onClose">;
type ModalProps<T extends IntrinsicTag> = { children?: ReactNode | undefined; onClose?: (() => void) | undefined; clickToExit?: boolean | undefined; modalStyle?: ModalSizeStyle | undefined; } & Omit<ComponentPropsWithoutRef<T>, "children" | "onClose">;
type PortalElementProps = Record<string, unknown>;

type OverlayPortalProps = { tag: IntrinsicTag; elementProps: PortalElementProps; children?: ReactNode; onClose?: (() => void) | undefined; clickToExit: boolean; };
type ModalPortalProps = OverlayPortalProps & { modalStyle: ModalSizeStyle; };

function OverlayPortal({ tag, elementProps, children, onClose, clickToExit }: OverlayPortalProps) {
    const id = useId();
    const onCloseRef = useRef(onClose);
    const { anchorRef, controller } = useNearestOverlayController();
    const { addOverlay, removeOverlay, overlays } = useOverlayContext();
    useEffect(() => { onCloseRef.current = onClose; }, [onClose]);
    useEffect(() => {
        addOverlay(id, null, () => onCloseRef.current?.(), clickToExit);
        return () => removeOverlay(id, false);
    }, [addOverlay, clickToExit, id, removeOverlay]);
    const exists = overlays.some(overlay => overlay.id === id);
    const Component = tag as ElementType;
    const content = createElement(Component, elementProps, children);
    return <><span ref={anchorRef} hidden />{controller && exists && createPortal(<OverlaySubContext.Provider value={{ quit: () => removeOverlay(id) }}><InternalOverlay overlayId={id}>{content}</InternalOverlay></OverlaySubContext.Provider>, controller)}</>;
}

function ModalPortal({ tag, elementProps, children, onClose, clickToExit, modalStyle }: ModalPortalProps) {
    const id = useId();
    const onCloseRef = useRef(onClose);
    const { anchorRef, controller } = useNearestOverlayController();
    const { addOverlay, removeOverlay, overlays } = useOverlayContext();
    useEffect(() => { onCloseRef.current = onClose; }, [onClose]);
    useEffect(() => {
        addOverlay(id, null, () => onCloseRef.current?.(), clickToExit);
        return () => removeOverlay(id, false);
    }, [addOverlay, clickToExit, id, removeOverlay]);
    const exists = overlays.some(overlay => overlay.id === id);
    const Component = tag as ElementType;
    const content = createElement(Component, elementProps, children);
    return <><span ref={anchorRef} hidden />{controller && exists && createPortal(<OverlaySubContext.Provider value={{ quit: () => removeOverlay(id) }}><InternalModal overlayId={id} style={modalStyle}>{content}</InternalModal></OverlaySubContext.Provider>, controller)}</>;
}

type OverlayComponents = { [K in IntrinsicTag]: ComponentType<OverlayProps<K>>; };
type ModalComponents = { [K in IntrinsicTag]: ComponentType<ModalProps<K>>; };

function createOverlayComponent<T extends IntrinsicTag>(tag: T) {
    return function OverlayComponent({ children, onClose, clickToExit = true, ...elementProps }: OverlayProps<T>) {
        return <OverlayPortal tag={tag} elementProps={elementProps as PortalElementProps} onClose={onClose} clickToExit={clickToExit}>{children}</OverlayPortal>;
    };
}

function createModalComponent<T extends IntrinsicTag>(tag: T) {
    return function ModalComponent({ children, onClose, clickToExit = true, modalStyle = { maxWidth: "40svw", maxHeight: "80svh" }, ...elementProps }: ModalProps<T>) {
        return <ModalPortal tag={tag} elementProps={elementProps as PortalElementProps} onClose={onClose} clickToExit={clickToExit} modalStyle={modalStyle}>{children}</ModalPortal>;
    };
}

const overlayComponents = new Map<IntrinsicTag, unknown>();
const modalComponents = new Map<IntrinsicTag, unknown>();

export const overlay = new Proxy({} as OverlayComponents, {
    get: (_target, property) => {
        if (typeof property !== "string") return undefined;
        const tag = property as IntrinsicTag;
        const cached = overlayComponents.get(tag);
        if (cached) return cached;
        const component = createOverlayComponent(tag);
        overlayComponents.set(tag, component);
        return component;
    },
});

export const modal = new Proxy({} as ModalComponents, {
    get: (_target, property) => {
        if (typeof property !== "string") return undefined;
        const tag = property as IntrinsicTag;
        const cached = modalComponents.get(tag);
        if (cached) return cached;
        const component = createModalComponent(tag);
        modalComponents.set(tag, component);
        return component;
    },
});
